import { FunctionComponent, JSX, useEffect, useState } from 'react';
import Tile from '../Shared/Tiles/TileV2';
import { Tab, Tabs } from '@lmig/lmds-react-tabs';
import { getRecentActivitiesLink, isPL, isProd } from 'Foundation/Configurations/GlobalConfiguration';
import Renewals from './PL/Renewals/Renewals';
import Billing from './PL/Billing/Billing';
import Policies from './PL/Policies/Policies';
import Claims from './PL/Claims/Claims';
import { useLocalStorage } from 'Utilities/UseStorage';
import Underwriting from './Underwriting';
import SCPolicies from './SC/Policies/SCPolicies';
import SCBilling from './SC/Billing/SCBilling';
import SCClaims from './SC/Claims/SCClaims';
import SCPremiumAudit from './SC/PremiumAudit/SCPremiumAudit';
import { ErrorBoundary } from 'react-error-boundary';

interface ITileActivityTab {
  label: string;
  body: JSX.Element;
}

const getTileFooterDateText = (activeTab: number, pl: boolean, prod: boolean): string | null => {
  if (pl) {
    // If activeTab is 1 (Billing), show last 7 days. 
    if (activeTab === 1) return 'Last 7 days';

    // If activeTab is 2 (Claims) and environment is not prod, show last 30 days. 
    if (activeTab === 2 && !prod) return 'Last 30 days';

    // If activeTab is 4 (Underwriting), do not show any date text.
    if (activeTab === 4) return null;

    // For all other cases in PL, show last 3 days.
    return 'Last 3 days';
  } else {
    // For BL, if activeTab is 3 (Premium audit) do not show any date text. 
    // For all other tabs, show last 30 days in non-prod and last 3 days in prod.
    if (activeTab !== 3) {
      return prod ? 'Last 3 days' : 'Last 30 days';
    } 
  } 

  return null;
};

const RecentActivitiesTile: FunctionComponent<any> = () => {
  const [activeTab, setActiveTab] = useLocalStorage<number>('recent-activities-active-tab', 0);
  const [recentActivitiesLink, setRecentActivitiesLink] = useState<string>(getRecentActivitiesLink());
  const [recentActivitiesLinkText, setRecentActivitiesLinkText] = useState<string>('All recent activity');

  const isPlValue = isPL();
  const tileFooterDateText = getTileFooterDateText(activeTab, isPlValue, isProd());

  {
    /* IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay */
  }
  const tabs: ITileActivityTab[] = isPlValue
    ? [
        {
          label: 'Renewals',
          body: <Renewals activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Billing',
          body: <Billing activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Claims',
          body: <Claims activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Policies',
          body: <Policies activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Underwriting',
          body: <Underwriting data-cs-mask />,
        },
      ]
    : [
        {
          label: 'Policies',
          body: <SCPolicies activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Billing',
          body: <SCBilling activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Claims',
          body: <SCClaims activeTab={activeTab} data-cs-mask />,
        },
        {
          label: 'Premium audit',
          body: <SCPremiumAudit activeTab={activeTab} data-cs-mask />,
        },
      ];

  useEffect(() => {
    if (!isPlValue && activeTab === 3) {
      setRecentActivitiesLink('');
      setRecentActivitiesLinkText('');
    } else {
      setRecentActivitiesLink(getRecentActivitiesLink());
      setRecentActivitiesLinkText('All recent activity');
    }
  }, [activeTab]);

  return (
    <div data-theme="evolved" className={`recent-activities-wrapper-` + (isPlValue ? 'pl' : 'bl')}>
      <Tile
        title={`My agency's customers`}
        headerLinkHref={recentActivitiesLink}
        headerLinkText={recentActivitiesLinkText}
        showStatCode={isPlValue}
      >
        <ErrorBoundary
          fallback={<div>An error has occurred in this component, please try reloading the page.</div>}
          onError={(error, info) => {console.error(`Error loading Recent Activities tab`, error, info);}}
        >
          <Tabs activeTab={activeTab} onTabActivated={(event, index) => setActiveTab({ data: index })} compact={true}>
            {tabs.map((tab) => (
                <Tab labelVisual={tab.label}>{tab.body}</Tab>
            ))}
          </Tabs>
        </ErrorBoundary> 
        {tileFooterDateText && <div className="tile-footer-date">{tileFooterDateText}</div>}
      </Tile>
    </div>
  );
};

export default RecentActivitiesTile;
